import * as React from "react";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbDivider,
  BreadcrumbButton,
  Tooltip,
  makeStyles,
  Button,
} from "@fluentui/react-components";
import {
  ChevronLeft20Regular,
  ChevronRight20Regular,
} from "@fluentui/react-icons";
import style from "./breadcrumbStyles.module.scss";
import { BreadCrumbDividerIcon } from "../../../modules/default-svg";
import useNavigation from "../../../utils/useNavigation";

const customDivider = () => {
  return (
    <span className="ms-1 me-1">
      <BreadCrumbDividerIcon />
    </span>
  );
};

const useStyles = makeStyles({
  tooltip: {
    backgroundColor: "#342c82",
    color: "#FFFFFF",
  },
});

const Breadcrumbs = (props) => {
  const { 
    breadcrumbItems, 
    navigateBackTo, 
    navigateBackward,
    navigateForward,
    hasNavigation,
    canGoBack,
    canGoForward,
  } = useNavigation();
  const styles = useStyles(); // Move hook to top level before any returns
  const path = window.location.pathname;

  // Don't show breadcrumbs on activity pages
  if (
    path.includes("/reminders/activity") ||
    path.includes("/notifications/activity") ||
    path.includes("/notifications/settings")
  ) {
    return null;
  }

  const handleClick = (breadcrumbItem) => {
    // Navigate back to the clicked breadcrumb item
    navigateBackTo({
      type: breadcrumbItem.type,
      id: breadcrumbItem.id,
      name: breadcrumbItem.name,
    });
  };

  const handleBackClick = () => {
    if (canGoBack) {
      navigateBackward();
    }
  };

  const handleForwardClick = () => {
    if (canGoForward) {
      navigateForward();
    }
  };

  const getToolTip = (type) => {
    switch (type) {
      case "Clients":
        return "Clients";
      case "Programs":
        return "Programs";
      case "Portfolios":
        return "Portfolios";
      case "Projects":
        return "Projects";
      case "Milestones":
        return "Milestones";
      case "clients":
        return "Client";
      case "program":
        return "Program";
      case "portfolio":
        return "Portfolio";
      case "projects":
        return "Project";
      case "ScopeItems":
        return "Scopes";
      case "scopeItems":
        return "Scopes";
      default:
        return "";
    }
  };

  // Don't render if there's no navigation
  if (!hasNavigation || !breadcrumbItems.length) {
    return null;
  }

  return (
    <>
      <div className={style.breadcrumbContainer}>
        {/* Back Navigation Button */}
        {canGoBack && (
          <Tooltip
            withArrow
            content={{
              children: "Navigate back in breadcrumb history",
              className: styles.tooltip,
            }}
            relationship="label"
          >
            <Button
              appearance="subtle"
              size="small"
              icon={<ChevronLeft20Regular />}
              onClick={handleBackClick}
              className={`${style.navButton} ${style.backButton}`}
            />
          </Tooltip>
        )}

        {/* Breadcrumb Items */}
        <Breadcrumb size="medium" className={style.breadCrumbWrapper}>
          {breadcrumbItems.map((breadcrumbItem, index) => (
            <React.Fragment key={`${breadcrumbItem.type}-${breadcrumbItem.id}`}>
              <BreadcrumbItem
                className={`${style.breadCrumbItems}`}
                onClick={() =>
                  breadcrumbItem.isClickable && handleClick(breadcrumbItem)
                }
                style={{
                  cursor: breadcrumbItem.isClickable ? "pointer" : "default",
                }}
              >
                <Tooltip
                  withArrow
                  content={{
                    children: getToolTip(breadcrumbItem.type) + " / " + breadcrumbItem.name,
                    className: styles.tooltip,
                  }}
                  relationship="label"
                  {...props}
                >
                  <BreadcrumbButton
                    className={`${breadcrumbItem.isActive && style.activeBread} ${
                      style.breadHeading
                    }`}
                  >
                    {breadcrumbItem.name}
                  </BreadcrumbButton>
                </Tooltip>
              </BreadcrumbItem>
              {index !== breadcrumbItems.length - 1 && (
                <BreadcrumbDivider as={customDivider} />
              )}
            </React.Fragment>
          ))}
        </Breadcrumb>

        {/* Forward Navigation Button */}
        {canGoForward && (
          <Tooltip
            withArrow
            content={{
              children: "Navigate forward in breadcrumb history",
              className: styles.tooltip,
            }}
            relationship="label"
          >
            <Button
              appearance="subtle"
              size="small"
              icon={<ChevronRight20Regular />}
              onClick={handleForwardClick}
              className={`${style.navButton} ${style.forwardButton}`}
            />
          </Tooltip>
        )}
      </div>
    </>
  );
};

export default Breadcrumbs;
